import { Trackable } from '@/components/common';
import { useContactForm } from '@/hooks';
import { cn } from '@/lib';
import { Form, Formik } from 'formik';
import {
    Loader2,
    ShieldCheck
} from 'lucide-react';
import * as Yup from 'yup';
import { FormInput } from './components';

interface TProps {
    formTitle?: string;
    textAreaPlaceholder?: string;
    className?: string;
}

const ContactUsForm = ({ className, formTitle="What brought you here?", textAreaPlaceholder="Share the details so we can help you out..." }: TProps) => {
    const validationSchema = Yup.object({
        name: Yup.string().required('Full Name is required'),
        email: Yup.string().email('Invalid email'),
        mobile: Yup.string()
            .matches(/^[0-9]{10}$/, 'Mobile number must be 10 digits')
            .required('Mobile number is required'),
        message: Yup.string().required('Message is required'),
    });

    const { handleSubmit } = useContactForm();

    return (
        <div className={cn("w-full lg:w-[450px] shrink-0", className)}>
            <div className="relative overflow-hidden rounded-2xl bg-white ring-1 ring-gray-900/5">
                <div className="h-2 w-full bg-gradient-to-r from-primary to-purple-500" />
                <div className="p-4 sm:p-6">
                    <div className="mb-8">
                        <h3 className="text-xl font-bold text-gray-900">Connect With Our Team</h3>
                        <p className="mt-2 text-xs text-gray-500">
                            Get expert guidance on ticketing, bookings, and visitor management for your attraction.
                        </p>
                    </div>

                    <Formik
                        initialValues={{
                            name: '',
                            email: '',
                            mobile: '',
                            message: '',
                            from: 'GREET_WEBSITE',
                        }}
                        validationSchema={validationSchema}
                        onSubmit={async (values, { resetForm }) => {
                            await handleSubmit({ values, resetForm });
                        }}
                    >
                        {({ isSubmitting, errors, touched }) => (
                            <Form className="space-y-5">
                                <FormInput
                                    errors={errors}
                                    touched={touched}
                                    label="Full Name"
                                    name="name"
                                    placeholder="Enter your full name"
                                    required
                                    disabled={isSubmitting}
                                />
                                <FormInput
                                    errors={errors}
                                    touched={touched}
                                    label="Mobile Number"
                                    name="mobile"
                                    type='tel'
                                    placeholder="Enter your mobile number"
                                    required
                                    disabled={isSubmitting}
                                />
                                {/* <FormInput
                                    errors={errors}
                                    touched={touched}
                                    label="Email Address"
                                    name="email"
                                    type='email'
                                    placeholder="Enter your email"
                                    required={false}
                                    disabled={isSubmitting}
                                /> */}
                                <FormInput
                                    errors={errors}
                                    touched={touched}
                                    label={formTitle}
                                    name="message"
                                    type='message'
                                    placeholder={textAreaPlaceholder}
                                    as="textarea"
                                    className='resize-none h-24'
                                    required
                                    disabled={isSubmitting}
                                />
                                <Trackable eventName="Submit" eventScope="Contact Form">
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={cn(
                                            "mt-2 flex w-full items-center justify-center rounded-lg bg-primary px-6 py-3.5 text-center text-xs font-bold text-white transition cursor-pointer focus:outline-none focus:ring-4 focus:ring-primary/30 disabled:opacity-50 disabled:cursor-not-allowed",
                                        )}
                                    >
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Sending...
                                            </>
                                        ) : (
                                            "Let's Get in Touch"
                                        )}
                                    </button>
                                </Trackable>
                                <div className="flex items-center justify-center gap-2 text-[10px] text-gray-400">
                                    <ShieldCheck className="h-3 w-3" />
                                    <span>Prefer to talk directly? Call us at <a href="tel:9031716589" className="font-semibold text-primary hover:underline">9031716589</a></span>
                                </div>
                            </Form>
                        )}
                    </Formik>
                </div>
            </div>
        </div>
    );
}

export default ContactUsForm;